import { Actions } from "@Context/common/general/context";
import { AnalyticsEvents } from "@Enums/index";
import { ReleaseNoteApis, ReleaseNoteRecord } from "@Shared/schemas/release-notes/releaseNoteSchemas";
import { useApi, useGlobalContext } from "@hooks/index";
import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";

// Define the context interface
interface ReleaseNotesContextType {
  notes: ReleaseNoteRecord[];
  hasUnseenNotes: boolean;
  loading: boolean;
  error: any;
  fetchReleaseNotes: (publishedOnly?: boolean, unseenOnly?: boolean) => Promise<void>;
  markAsSeen: (releaseNoteId: number, version?: string) => Promise<void>;
  deleteReleaseNote: (releaseNoteId: number) => Promise<void>;
  processReleaseNoteImages: (element: HTMLElement) => void;
}

// Create the context
export const ReleaseNotesContext = createContext<ReleaseNotesContextType | undefined>(undefined);

// Create the provider component
interface ReleaseNotesProviderProps {
  children: ReactNode;
  autoFetch?: boolean; // Whether to automatically fetch notes on mount
}

export const ReleaseNotesProvider: React.FC<ReleaseNotesProviderProps> = ({ children, autoFetch = true }) => {
  const { dispatch, globalState } = useGlobalContext();

  // State variables
  const [notes, setNotes] = useState<ReleaseNoteRecord[]>([]);
  const [hasUnseenNotes, setHasUnseenNotes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  // Setup API hooks
  const { call: getReleaseNotes, loading: loadingNotes } = useApi<typeof ReleaseNoteApis.ReleaseNotes.Get>({
    api: ReleaseNoteApis.ReleaseNotes.Get,
    request: { published_only: true, unseen_only: true },
  });

  const { call: markNoteAsSeen } = useApi<typeof ReleaseNoteApis.ReleaseNotes.MarkAsSeen>({
    api: ReleaseNoteApis.ReleaseNotes.MarkAsSeen,
    request: { release_note_id: 0 }, // This will be overridden when called
  });

  const { call: deleteNote } = useApi<typeof ReleaseNoteApis.ReleaseNotes.Delete>({
    api: ReleaseNoteApis.ReleaseNotes.Delete,
    request: { id: 0 }, // This will be overridden when called
  });

  // Update loading state when API loading state changes
  useEffect(() => {
    setLoading(loadingNotes);
  }, [loadingNotes]);

  // Process images in release notes to add access tokens
  const processReleaseNoteImages = (element: HTMLElement) => {
    if (!element) return;

    const images = element.getElementsByTagName("img");
    Array.from(images).forEach((img) => {
      const originalSrc = img.getAttribute("src");
      if (originalSrc?.includes(import.meta.env.VITE_BUNNY_RELEASE_NOTES_IMG_UPLOAD_URL)) {
        img.src = `${originalSrc}?access_token=${import.meta.env.VITE_BUNNY_RELEASE_NOTES_IMG_UPLOAD_API_KEY}`;
      }
    });
  };

  // Fetch release notes
  const fetchReleaseNotes = async (publishedOnly = true, unseenOnly = true) => {
    try {
      setLoading(true);
      const response = await getReleaseNotes({
        published_only: publishedOnly,
        unseen_only: unseenOnly,
      });

      if (response && response.data) {
        setNotes(response.data);
        setHasUnseenNotes(response.data.length > 0);
      }
    } catch (err) {
      setError(err);
    }
  };

  // Mark a release note as seen
  const markAsSeen = async (releaseNoteId: number, version?: string) => {
    try {
      await markNoteAsSeen({ release_note_id: releaseNoteId });

      // Dispatch analytics event
      dispatch({
        type: Actions.AppActions.AnalyticsEvent,
        payload: {
          type: AnalyticsEvents.release_note_viewed,
          data: {
            release_note_id: releaseNoteId,
            release_note_version: version,
          },
        },
      });

      // Update local state
      const updatedNotes = notes.filter((note) => note.id !== releaseNoteId);
      setNotes(updatedNotes);
      setHasUnseenNotes(updatedNotes.length > 0);
    } catch (err) {
      setError(err);
    }
  };

  // Delete a release note
  const deleteReleaseNote = async (releaseNoteId: number) => {
    try {
      await deleteNote({ id: releaseNoteId });

      // Update local state by removing the deleted note
      const updatedNotes = notes.filter((note) => note.id !== releaseNoteId);
      setNotes(updatedNotes);
      setHasUnseenNotes(updatedNotes.length > 0);
    } catch (err) {
      setError(err);
    }
  };

  // Fetch release notes when a profile is switched
  useEffect(() => {
    if (!globalState.user.dbId) return;
    fetchReleaseNotes();
  }, [globalState.user.dbId]);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchReleaseNotes();
    }
  }, [autoFetch]);

  return (
    <ReleaseNotesContext.Provider
      value={{
        notes,
        hasUnseenNotes,
        loading,
        error,
        fetchReleaseNotes,
        markAsSeen,
        deleteReleaseNote,
        processReleaseNoteImages,
      }}
    >
      {children}
    </ReleaseNotesContext.Provider>
  );
};

/**
 * Hook for accessing and managing release notes across different apps
 * @returns {ReleaseNotesContextType} The release notes context
 */
export const useReleaseNotes = (): ReleaseNotesContextType => {
  const context = useContext(ReleaseNotesContext);

  if (context === undefined) {
    throw new Error("useReleaseNotes must be used within a ReleaseNotesProvider");
  }

  return context;
};
